import { MAX_BOOKING_QUESTION_LABEL_CHARS } from '@shared/core/appointment-types'
import classNames from 'classnames'
import { X } from 'lucide-react'
import { useState, type ChangeEvent } from 'react'
import {
  clampToCodePoints,
  createDraft,
  draftFromQuestion,
  hasDraftErrors,
  isChoiceType,
  validateDraft,
  visibleDraftErrors,
  type QuestionDraft,
  type QuestionTile,
} from '../booking-questions-editor.domain'
import { preventSubmitOnEnter } from '../booking-questions-editor.utils'
import { OptionsEditor } from '../options-editor'
import { QuestionCardFooter } from '../question-card-footer'
import { QuestionTypeTiles } from '../question-type-tiles'
import type { QuestionCardFieldsProps, QuestionCardProps } from './question-card.interface'
import classes from './question-card.module.scss'

const ADD_CARD_TITLE = 'What kind of answer?'
const QUESTION_PLACEHOLDER = 'e.g. Which service are you interested in?'

export function QuestionCard({ mode, question, idPrefix, onConfirm, onCancel, onDelete }: QuestionCardProps) {
  const [tile, setTile] = useState<QuestionTile | null>(null)
  const [draft, setDraft] = useState<QuestionDraft | null>(() => (question ? draftFromQuestion(question) : null))
  const [attempted, setAttempted] = useState(false)
  const isAdd = mode === 'add'

  const selectTile = (next: QuestionTile) => {
    setTile(next)
    setDraft(createDraft(next, draft ?? undefined))
  }

  const confirm = () => {
    setAttempted(true)
    if (draft && !hasDraftErrors(validateDraft(draft))) onConfirm(draft)
  }

  return (
    <div
      className={classes.root}
      role="group"
      aria-label={isAdd ? 'New question' : 'Edit question'}
      data-testid={isAdd ? 'add-question-card' : 'edit-question-card'}
    >
      {isAdd && (
        <div className={classes.header}>
          <div className={classes.title}>{ADD_CARD_TITLE}</div>
          <button type="button" className={classes.closeButton} aria-label="Close" onClick={onCancel}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      )}
      {isAdd && <QuestionTypeTiles selectedTileId={tile?.id ?? null} onSelect={selectTile} />}
      {draft && (
        <QuestionCardFields
          draft={draft}
          attempted={attempted}
          idPrefix={idPrefix}
          confirmLabel={isAdd ? 'Add question' : 'Done'}
          onChange={setDraft}
          onConfirm={confirm}
          onCancel={onCancel}
          onDelete={onDelete}
        />
      )}
    </div>
  )
}

function QuestionCardFields({ draft, attempted, idPrefix, confirmLabel, ...actions }: QuestionCardFieldsProps) {
  const errors = visibleDraftErrors(validateDraft(draft), attempted)
  const labelInputId = `${idPrefix}-label`
  const labelErrorId = `${idPrefix}-label-error`
  const changeLabel = (event: ChangeEvent<HTMLInputElement>) =>
    actions.onChange({ ...draft, label: clampToCodePoints(event.target.value, MAX_BOOKING_QUESTION_LABEL_CHARS) })

  return (
    <>
      <div className={classes.field}>
        <label htmlFor={labelInputId} className={classes.fieldLabel}>
          Question
        </label>
        <input
          id={labelInputId}
          className={classNames(classes.input, { [classes.inputError]: errors.label })}
          value={draft.label}
          placeholder={QUESTION_PLACEHOLDER}
          aria-invalid={errors.label ? true : undefined}
          aria-describedby={errors.label ? labelErrorId : undefined}
          onChange={changeLabel}
          onKeyDown={preventSubmitOnEnter}
          autoFocus
        />
        {errors.label && (
          <div id={labelErrorId} className={classes.error}>
            {errors.label}
          </div>
        )}
      </div>
      {isChoiceType(draft.type) && (
        <OptionsEditor draft={draft} errors={errors} idPrefix={idPrefix} onChange={actions.onChange} />
      )}
      <QuestionCardFooter
        requiredId={`${idPrefix}-required`}
        required={draft.required}
        confirmLabel={confirmLabel}
        onRequiredChange={(required) => actions.onChange({ ...draft, required })}
        onConfirm={actions.onConfirm}
        onCancel={actions.onCancel}
        onDelete={actions.onDelete}
      />
    </>
  )
}
